const {
  getMetric,
  clearMetric,
  appendMetric,
  checkMetric,
} = require('../collector');
const { sendWithFetch } = require('../utils/send');
const { getUserId, getPath } = require('../utils/user-id');
const { prepareDeviceType } = require('../builder/commons');
const {
  endpoint,
  PAYLOAD_SIZE,
  MAX_MLDATA_WAITING_TIME,
  MAX_CHUNKS_SIZE,
  MAX_FLUSH_TIME,
  CHUNKS_EXPIRATION_TIME,
} = require('../../constants/session-replay');
const {
  SESSION_ID,
  REQUEST_ID,
  APP_NAME,
  SCOPE_NAME,
  USER_AGENT,
  DEVICE,
  WEBVIEW,
  CUSTOM_ATTRIBUTES,
  TRACEABILITY_ID,
  SESSION_REPLAY,
  SITE_ID,
} = require('../../constants/metrics');
const { objectOperator } = require('../utils/spread');
const {
  addToBuffer,
  getBuffer,
  clearBuffer,
} = require('../storage-buffer');

const bufferConfig = {
  flushTime: MAX_FLUSH_TIME,
  size: MAX_CHUNKS_SIZE,
  limit: PAYLOAD_SIZE,
  maxAge: CHUNKS_EXPIRATION_TIME,
  isFirstRun: true,
};

let tickerTimeout = null;

const buildCtx = () => ({
  session_id: getMetric(SESSION_ID),
  view_id: getMetric(REQUEST_ID),
  app_name: getMetric(APP_NAME),
  fury_context_name: getMetric(SCOPE_NAME),
});

const availableLocations = ['mla', 'mlb', 'mlm'];
const defaultLocation = 'rola';

const buildStorageLocation = () => {
  const siteId = (getMetric(SITE_ID) || '').toLowerCase();

  return availableLocations.some(location => location === siteId)
    ? siteId
    : defaultLocation;
};

const processMetadata = () => {
  const metadata = getMetric(CUSTOM_ATTRIBUTES);
  let metadataPayload = null;
  let fallbackUserId = null;

  if (metadata) {
    metadataPayload = metadata;

    if (metadata.userId) {
      fallbackUserId = metadata.userId;
    }
  }

  return { metadataPayload, fallbackUserId };
};

const buildPayload = (ctx, snapshot) => {
  const { metadataPayload, fallbackUserId } = processMetadata();

  const payload = objectOperator([{}, ctx, {
    device_timestamp: Date.now(),
    user_id: getUserId(fallbackUserId),
    view_name: getPath(),
    mime_type: 'application/json',
    asset: snapshot,
    metadata: metadataPayload,
  }]);

  const traceabilityId = getMetric(TRACEABILITY_ID);
  if (traceabilityId) {
    payload.traceability_id = traceabilityId;
  }

  const device = prepareDeviceType({
    [USER_AGENT]: getMetric(USER_AGENT),
  }, {
    device: getMetric(DEVICE),
    webview: getMetric(WEBVIEW),
  });

  if (device) {
    payload.device_platform = `/${device}`;
  }

  return { data: payload };
};

const makeSnapshot = (key, resetTimeout = true) => {
  const snapshot = getMetric(key);

  if (resetTimeout) {
    clearTimeout(tickerTimeout);
    clearMetric(key);
  }

  return snapshot;
};

const melidataTask = {
  hasReached: false,
  ref: null,
};

const waitForMelidata = () => {
  melidataTask.ref = setTimeout(() => {
    melidataTask.hasReached = true;
  }, MAX_MLDATA_WAITING_TIME);
};

const isMelidataNamespaceReady = () => {
  if (melidataTask.hasReached) {
    return true;
  }

  const isReady = typeof window.melidata === 'undefined'
    || (window.melidata_namespace
      && window.melidata_namespace.actual_track
      && Object.keys(window.melidata_namespace.actual_track).length > 0);

  if (isReady && melidataTask.ref) {
    clearTimeout(melidataTask.ref);
  }

  if (isReady) {
    return true;
  }

  if (!melidataTask.hasReached && !melidataTask.ref) {
    waitForMelidata();
  }

  return false;
};

const send = (key) => {
  if (!isMelidataNamespaceReady()) {
    return;
  }

  const ctx = buildCtx();
  const snapshot = makeSnapshot(key);

  if (!snapshot || !ctx) {
    return;
  }

  const payload = buildPayload(ctx, snapshot);

  sendWithFetch(`${endpoint(buildStorageLocation())}`, JSON.stringify(payload), false);
};

const tryToSend = (key) => {
  const data = getMetric(key);

  if (JSON.stringify(data).length >= bufferConfig.limit || data.length >= bufferConfig.size) {
    send(key);
  }
};

const increment = (key, data) => {
  appendMetric(key, data);
  tryToSend(key);
};

const run = (key) => {
  tickerTimeout = setTimeout(() => {
    send(key);
  }, bufferConfig.flushTime);
};

const sendPending = () => {
  const { payload, url } = getBuffer(SESSION_REPLAY, { payload: null, url: null });

  if (payload) {
    clearBuffer(SESSION_REPLAY);
    sendWithFetch(`${url}`, JSON.stringify(payload), false);
  }
};

const savePending = () => {
  const ctx = buildCtx();
  const snapshot = makeSnapshot(SESSION_REPLAY);

  if (!snapshot || !ctx) {
    return;
  }

  const payload = buildPayload(ctx, snapshot);
  const url = endpoint(buildStorageLocation());

  addToBuffer(SESSION_REPLAY, { payload, url }, bufferConfig.maxAge);
};

const createBuffer = (key, data) => {
  if (bufferConfig.isFirstRun) {
    sendPending();
    bufferConfig.isFirstRun = false;
  }

  appendMetric(key, data);

  run(key);
};

const monitor = (key, data) => {
  if (checkMetric(key)) {
    increment(key, data);
  } else {
    createBuffer(key, data);
  }
};

module.exports = { monitor, send, savePending, sendPending, processMetadata };
